import type { StudiesFilterQueryVariables } from 'graphql/research/types-and-hooks';

import React, { useMemo, useState } from 'react';
import { useToast, Paragraph } from '@leafwell/components';

import { useStudiesFilterQuery } from 'graphql/research/types-and-hooks';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { useDasboardContext } from 'contexts/dashboard-context';

import ArrowButton from './arrow-button';
import HeadingBody from './heading-body';
import LinkArrow from './link-arrow';
import Line from './line';

const STUDIES_PER_PAGE = 10;

const DashboardStudies: React.FC<{ id: string }> = ({ id }) => {
  const { addToast } = useToast();
  const { translations } = useWordPressContext();
  const {
    filters,
    initialData: {
      studies: { paginated: initialData },
    },
    studies: { paginated, total },
  } = useDasboardContext();
  const [studiesList, updateStudiesList] = useState(paginated);
  const [pagination, updatePagination] = useState<
    Omit<StudiesFilterQueryVariables, 'filters'>
  >({
    offset: 0,
    limit: STUDIES_PER_PAGE,
  });

  const showInitialData = useMemo(
    () => !filters && pagination.offset <= 0,
    [filters, pagination.offset],
  );

  const { loading } = useStudiesFilterQuery({
    variables: { ...pagination, filters },
    skip: showInitialData,
    onCompleted: data => updateStudiesList(prev => [...prev, ...data.nodes]),
    onError: error => {
      addToast({
        content: error.message,
        key: 'dashboard-studies-list-error',
        type: 'negative',
        shouldRemove: true,
      });
    },
  });

  useMemo(() => {
    if (showInitialData) {
      updateStudiesList(initialData);
    }
  }, [initialData, showInitialData]);

  return studiesList.length ? (
    <section {...{ id, className: 'grid gap-y-4' }}>
      <HeadingBody
        {...{
          level: '4',
          title: `${total} ${
            total !== 1 ? translations['studies'] : translations['study']
          }`,
        }}
      />
      <Paragraph size="small" className="font-medium -mb-2">
        {translations['title']}
      </Paragraph>
      <Line />
      {studiesList.map(({ link, title }, key) => (
        <LinkArrow
          {...{
            key,
            uri: link,
            showLine: false,
            title,
            variant: 'highlight',
            target: '_blank',
            rel: 'noopener noreferrer',
          }}
        />
      ))}
      {total > studiesList.length && (
        <ArrowButton
          {...{
            className: 'mt-3 justify-self-center',
            classNameImage: 'rotate-90',
            loading,
            title: translations['See more results'],
            onClick: () => {
              updatePagination(prev => ({
                ...prev,
                offset: prev.offset + prev.limit,
              }));
            },
          }}
        />
      )}
    </section>
  ) : null;
};

export default DashboardStudies;
